import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { 
  Users, 
  Send, 
  Trash2,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GerenciarFitCultural = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [concluidas, setConcluidas] = useState([]);
  const [modalAplicarAberto, setModalAplicarAberto] = useState(false);
  const [modalAplicarExternoAberto, setModalAplicarExternoAberto] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    avaliacao_id: '',
    colaboradores: []
  });

  // Form state para avaliação externa
  const [formDataExterno, setFormDataExterno] = useState({
    avaliacao_id: '',
    nome_candidato: '',
    email_candidato: '',
    cargo_vaga: ''
  });

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  const carregarDadosIniciais = async () => {
    await carregarAvaliacoes();
    await carregarFuncionarios();
    await carregarPendentes();
    await carregarConcluidas();
  };

  const carregarAvaliacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fit-cultural/avaliacoes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar apenas avaliações ativas
      const avaliacoesAtivas = (response.data || []).filter(a => a.ativa);
      setAvaliacoes(avaliacoesAtivas);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      setAvaliacoes([]);
    }
  };

  const carregarFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      setFuncionarios([]);
    }
  };

  const carregarPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fit-cultural/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendentes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar pendentes:', error);
      setPendentes([]);
    }
  };

  const carregarConcluidas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fit-cultural/concluidas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConcluidas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar concluídas:', error);
      setConcluidas([]);
    }
  };

  const handleAplicarAvaliacao = async () => {
    if (!formData.avaliacao_id) {
      toast.error('Selecione uma avaliação');
      return;
    }
    if (formData.colaboradores.length === 0) {
      toast.error('Selecione pelo menos um colaborador');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/fit-cultural/aplicar`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Avaliação aplicada para ${formData.colaboradores.length} colaborador(es)`);
      setModalAplicarAberto(false);
      setFormData({ avaliacao_id: '', colaboradores: [] });
      await carregarPendentes();
    } catch (error) {
      console.error('Erro ao aplicar avaliação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao aplicar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const handleAplicarAvaliacaoExterna = async () => {
    if (!formDataExterno.avaliacao_id) {
      toast.error('Selecione uma avaliação');
      return;
    }
    if (!formDataExterno.nome_candidato) {
      toast.error('Informe o nome do candidato');
      return;
    }
    if (!formDataExterno.email_candidato) {
      toast.error('Informe o e-mail do candidato');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/fit-cultural/aplicar-externa`,
        formDataExterno,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`E-mail de avaliação enviado para ${formDataExterno.email_candidato}`);
      setModalAplicarExternoAberto(false);
      setFormDataExterno({ avaliacao_id: '', nome_candidato: '', email_candidato: '', cargo_vaga: '' });
      await carregarPendentes();
    } catch (error) {
      console.error('Erro ao aplicar avaliação externa:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação externa');
    } finally {
      setLoading(false);
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/fit-cultural/avaliacoes-aplicadas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Avaliação excluída com sucesso');
      await carregarPendentes();
      await carregarConcluidas();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir avaliação');
    }
  };

  const toggleColaborador = (id) => {
    setFormData(prev => {
      const colaboradores = prev.colaboradores.includes(id)
        ? prev.colaboradores.filter(c => c !== id)
        : [...prev.colaboradores, id];
      return { ...prev, colaboradores };
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Fit Cultural</h1>
          <p className="text-gray-600 mt-1">Gerenciar avaliações de fit cultural</p>
        </div>
      </div>

      <Tabs defaultValue="aplicar" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="aplicar">
            <Send className="h-4 w-4 mr-2" />
            Aplicar Avaliações
          </TabsTrigger>
          <TabsTrigger value="pendentes">
            <Clock className="h-4 w-4 mr-2" />
            Pendentes ({pendentes.length})
          </TabsTrigger>
          <TabsTrigger value="concluidas">
            <CheckCircle className="h-4 w-4 mr-2" />
            Concluídas ({concluidas.length})
          </TabsTrigger>
        </TabsList>

        {/* ABA APLICAR */}
        <TabsContent value="aplicar" className="space-y-4">
          <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                <FileText className="h-6 w-6 text-purple-600" />
                Aplicar Avaliação para Colaboradores
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button 
                onClick={() => setModalAplicarAberto(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Send className="h-4 w-4 mr-2" />
                Aplicar Avaliação
              </Button>
              <Button 
                onClick={() => setModalAplicarExternoAberto(true)}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Mail className="h-4 w-4 mr-2" />
                Aplicar Avaliação Externa
              </Button>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="text-2xl bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Avaliações Disponíveis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {avaliacoes.length === 0 ? (
                <p className="text-gray-500">Nenhuma avaliação disponível</p>
              ) : (
                <div className="space-y-4">
                  {avaliacoes.map(aval => (
                    <div 
                      key={aval.id} 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-bold text-xl text-white mb-2">{aval.nome}</h3>
                          <p className="text-blue-50 text-sm mb-2">{aval.descricao}</p>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-blue-100" />
                            <p className="text-sm text-blue-100 font-medium">
                              {aval.perguntas?.length || 0} perguntas
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-green-500 text-white border-none shadow-md">
                          Ativa
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA PENDENTES */}
        <TabsContent value="pendentes" className="space-y-4">
          <Card className="border-none shadow-lg bg-gradient-to-br from-amber-50 to-orange-50">
            <CardHeader>
              <CardTitle className="text-2xl bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Avaliações Pendentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendentes.length === 0 ? (
                <p className="text-gray-500">Nenhuma avaliação pendente</p>
              ) : (
                <div className="space-y-4">
                  {pendentes.map(item => {
                    const isCandidato = item.tipo_avaliacao === 'candidato';
                    const gradientClass = isCandidato 
                      ? "bg-gradient-to-r from-cyan-500 to-blue-500" 
                      : "bg-gradient-to-r from-amber-500 to-orange-500";
                    const textColorClass = isCandidato ? "text-cyan-50" : "text-amber-50";
                    
                    return (
                      <div 
                        key={item.id} 
                        className={`${gradientClass} p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-xl text-white mb-2">{item.avaliacao_nome}</h3>
                            <div className="space-y-1 mb-3">
                              <p className={`${textColorClass} text-sm flex items-center gap-2`}>
                                {isCandidato ? <Mail className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                                {item.colaborador_nome}
                                {isCandidato && (
                                  <Badge className="bg-white/20 text-white border-none text-xs ml-2">
                                    Candidato
                                  </Badge>
                                )}
                              </p>
                              {isCandidato && item.email_candidato && (
                                <p className={`${textColorClass} text-xs flex items-center gap-2`}>
                                  📧 {item.email_candidato}
                                </p>
                              )}
                              {isCandidato && item.cargo_vaga && (
                                <p className={`${textColorClass} text-xs flex items-center gap-2`}>
                                  💼 {item.cargo_vaga}
                                </p>
                              )}
                              <p className={`${textColorClass} text-sm flex items-center gap-2`}>
                                <Calendar className="h-4 w-4" />
                                Aplicada em: {new Date(item.aplicada_em).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            <Badge className="bg-yellow-400 text-gray-900 border-none shadow-md font-semibold">
                              <Clock className="h-3 w-3 mr-1" />
                              Pendente
                            </Badge>
                            {user?.is_admin && (
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleExcluir(item.id)}
                                className="bg-red-500 hover:bg-red-600 shadow-md"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA CONCLUÍDAS */}
        <TabsContent value="concluidas" className="space-y-4">
          <Card className="border-none shadow-lg bg-gradient-to-br from-emerald-50 to-green-50">
            <CardHeader>
              <CardTitle className="text-2xl bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                Avaliações Concluídas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {concluidas.length === 0 ? (
                <p className="text-gray-500">Nenhuma avaliação concluída</p>
              ) : (
                <div className="space-y-4">
                  {concluidas.map(item => {
                    const isCandidato = item.tipo_avaliacao === 'candidato';
                    const gradientClass = isCandidato 
                      ? "bg-gradient-to-r from-cyan-500 to-blue-500" 
                      : "bg-gradient-to-r from-emerald-500 to-green-500";
                    const textColorClass = isCandidato ? "text-cyan-50" : "text-emerald-50";
                    
                    return (
                      <div 
                        key={item.id} 
                        className={`${gradientClass} p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-xl text-white mb-2">{item.avaliacao_nome}</h3>
                            <div className="space-y-1 mb-3">
                              <p className={`${textColorClass} text-sm flex items-center gap-2`}>
                                {isCandidato ? <Mail className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                                {item.colaborador_nome}
                                {isCandidato && (
                                  <Badge className="bg-white/20 text-white border-none text-xs ml-2">
                                    Candidato
                                  </Badge>
                                )}
                              </p>
                              {isCandidato && item.email_candidato && (
                                <p className={`${textColorClass} text-xs flex items-center gap-2`}>
                                  📧 {item.email_candidato}
                                </p>
                              )}
                              {isCandidato && item.cargo_vaga && (
                                <p className={`${textColorClass} text-xs flex items-center gap-2`}>
                                  💼 {item.cargo_vaga}
                                </p>
                              )}
                              <p className={`${textColorClass} text-sm flex items-center gap-2`}>
                                <Calendar className="h-4 w-4" />
                                Concluída em: {new Date(item.concluida_em).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                            {item.ifc_calculado !== undefined && (
                              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 space-y-1">
                                <p className="text-sm font-bold text-white flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4" />
                                  IFC Final: {(item.ifc_calculado * 100).toFixed(1)}%
                                </p>
                                {item.fator_confiabilidade !== undefined && item.fator_confiabilidade < 1.0 && (
                                  <p className="text-xs text-yellow-100">
                                    ⚠️ Confiabilidade: {(item.fator_confiabilidade * 100).toFixed(0)}%
                                    {item.flags_antitrapaça && item.flags_antitrapaça.length > 0 && (
                                      <span className="ml-1">
                                        ({item.flags_antitrapaça.join(', ')})
                                      </span>
                                    )}
                                  </p>
                                )}
                                {item.ifc_bruto !== undefined && item.ifc_bruto !== item.ifc_calculado && (
                                  <p className="text-xs text-emerald-100">
                                    IFC Bruto: {(item.ifc_bruto * 100).toFixed(1)}%
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            <Badge className="bg-white text-green-600 border-none shadow-md font-semibold">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Concluída
                            </Badge>
                            {item.flags_antitrapaça && item.flags_antitrapaça.length > 0 && (
                              <Badge className="bg-yellow-400 text-gray-900 border-none shadow-md text-xs">
                                ⚠️ Suspeita
                              </Badge>
                            )}
                            {user?.is_admin && (
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleExcluir(item.id)}
                                className="bg-red-500 hover:bg-red-600 shadow-md"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MODAL APLICAR AVALIAÇÃO */}
      <Dialog open={modalAplicarAberto} onOpenChange={setModalAplicarAberto}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aplicar Avaliação</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Selecione a Avaliação</Label>
              <select 
                className="w-full border rounded p-2 mt-1"
                value={formData.avaliacao_id}
                onChange={(e) => setFormData(prev => ({ ...prev, avaliacao_id: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {avaliacoes.map(aval => (
                  <option key={aval.id} value={aval.id}>
                    {aval.nome} ({aval.perguntas?.length || 0} perguntas)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Selecione os Colaboradores</Label>
              <div className="border rounded p-3 mt-1 max-h-60 overflow-y-auto space-y-2">
                {funcionarios.map(func => (
                  <div key={func.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`func-${func.id}`}
                      checked={formData.colaboradores.includes(func.id)}
                      onChange={() => toggleColaborador(func.id)}
                      className="cursor-pointer"
                    />
                    <label htmlFor={`func-${func.id}`} className="cursor-pointer">
                      {func.name || func.nome} {func.position && `- ${func.position}`}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {formData.colaboradores.length} colaborador(es) selecionado(s)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAplicarAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAplicarAvaliacao} disabled={loading}>
              {loading ? 'Aplicando...' : 'Aplicar Avaliação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL APLICAR AVALIAÇÃO EXTERNA */}
      <Dialog open={modalAplicarExternoAberto} onOpenChange={setModalAplicarExternoAberto}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-cyan-600" />
              Aplicar Avaliação Externa (Candidato)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Selecione a Avaliação *</Label>
              <select 
                className="w-full border rounded p-2 mt-1"
                value={formDataExterno.avaliacao_id}
                onChange={(e) => setFormDataExterno(prev => ({ ...prev, avaliacao_id: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {avaliacoes.map(aval => (
                  <option key={aval.id} value={aval.id}>
                    {aval.nome} ({aval.perguntas?.length || 0} perguntas)
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-cyan-50 border-l-4 border-cyan-500 p-4 rounded">
              <p className="text-sm text-cyan-800">
                <strong>ℹ️ Avaliação Externa:</strong> O candidato receberá um e-mail com um link válido por 7 dias para responder a avaliação.
              </p>
            </div>

            <div>
              <Label>Nome do Candidato *</Label>
              <input
                type="text"
                className="w-full border rounded p-2 mt-1"
                placeholder="Ex: João Silva"
                value={formDataExterno.nome_candidato}
                onChange={(e) => setFormDataExterno(prev => ({ ...prev, nome_candidato: e.target.value }))}
              />
            </div>

            <div>
              <Label>E-mail do Candidato *</Label>
              <input
                type="email"
                className="w-full border rounded p-2 mt-1"
                placeholder="Ex: joao.silva@email.com"
                value={formDataExterno.email_candidato}
                onChange={(e) => setFormDataExterno(prev => ({ ...prev, email_candidato: e.target.value }))}
              />
            </div>

            <div>
              <Label>Cargo/Vaga (Opcional)</Label>
              <input
                type="text"
                className="w-full border rounded p-2 mt-1"
                placeholder="Ex: Desenvolvedor Full-Stack"
                value={formDataExterno.cargo_vaga}
                onChange={(e) => setFormDataExterno(prev => ({ ...prev, cargo_vaga: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAplicarExternoAberto(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAplicarAvaliacaoExterna} 
              disabled={loading}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              {loading ? 'Enviando...' : 'Enviar Avaliação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GerenciarFitCultural;
